/**
 * Free-Premium gift code carried on the QR card URL, e.g.
 * start.bonded.studio/?app=for-couples&code=BONDED-XXXX-XXXX. We capture it on the
 * landing (before the multi-step flow rewrites the URL) and persist it for the whole
 * in-tab session, so the offer step — after the visitor has claimed an account — can
 * redeem it and grant free Premium without ever opening the paid Paddle checkout.
 *
 * Mirrors lib/audience.ts. The code is a secret, so it lives only in sessionStorage
 * and is never written to funnel analytics attribution.
 */
const STORAGE_KEY = 'bonded_funnel_gift_code'

const normalize = (raw: string): string => raw.trim().toUpperCase().slice(0, 64)

/**
 * Capture `?code=` from the URL into sessionStorage (URL wins over a stored value) and
 * return the effective code. Call once on the landing. Safe to call with no code.
 */
export function resolveGiftCode(search: string = window.location.search): string {
  const fromUrl = normalize(new URLSearchParams(search).get('code') ?? '')
  if (fromUrl) {
    try {
      sessionStorage.setItem(STORAGE_KEY, fromUrl)
    } catch {
      /* storage disabled — fall through to returning the URL value */
    }
    return fromUrl
  }
  return getGiftCode()
}

/** Read the captured code (for the offer step). Empty string when none. */
export function getGiftCode(): string {
  try {
    return sessionStorage.getItem(STORAGE_KEY) ?? ''
  } catch {
    return ''
  }
}

/** Drop the code after a successful redemption so a refresh doesn't retry it. */
export function clearGiftCode(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY)
  } catch {
    /* no-op */
  }
}
